/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

/* eslint-disable @typescript-eslint/no-base-to-string */

// Este codigo fue generado por Github Copilot Chat
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  OnChanges,
  AfterViewChecked,
  OnDestroy,
  NgZone,
  AfterViewInit
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { TableActionType } from './tableActionTypes';
import { debounceTime, Subscription } from 'rxjs';
import { SelectOption } from '../select/options.type';
import {
  FilterTableType,
  OrderEnum,
  SortType,
  ColumnConfig
} from './table.type';
import { camelCaseToSnake } from '../../helpers/commonFunctions';
import { HandleFormControlPipe } from '../../pipes/handle-form-control.pipe';
import { CollaboratorsPipe } from '../../pipes/collaborators.pipe';
import { SelectComponent } from '../select/select.component';
import { InputTextComponent } from '../input-text/input-text.component';
import { InputCheckComponent } from '../input-check/input-check.component';
import { ModalComponent } from '../modal/modal.component';
import { SuraAvatarCollapsibleComponent } from '../sura-avatar-collapsible/sura-avatar-collapsible.component';
import { ShowTooltipOnOverflowDirective } from '../../directives/show-tooltip-on-overflow.directive';
import { ActionMenuComponent,
  ActionMenuConfig,
  ActionMenuEvent
} from '../action-menu/action-menu.component';
import { ExamTypeBadgePipe } from '../../pipes/exam-type-badge.pipe';

/**
 * Type alias for date parsing return values
 */
type DateValue = Date | string | number;

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HandleFormControlPipe,
    CollaboratorsPipe,
    InputCheckComponent,
    SelectComponent,
    InputTextComponent,
    ModalComponent,
    SuraAvatarCollapsibleComponent,
    ShowTooltipOnOverflowDirective,
    ActionMenuComponent,
    ExamTypeBadgePipe
  ],
  providers: [DatePipe],
  templateUrl: './app-table.component.html',
  styleUrls: ['./app-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableComponent<T extends Record<string, unknown>>
  implements OnInit, OnChanges, AfterViewChecked, AfterViewInit, OnDestroy
{
  private static readonly DEFAULT_TIMEZONE = 'America/Bogota';

  @Input() items: T[] = [];
  @Input() visibleColumns: ColumnConfig[] = [];

  @Input() showActions = true;
  @Input() actionsPosition: 'left' | 'right' = 'right';
  @Input() tableName = '';
  @Input() orderByDefault = true;
  @Input() noDataMessage = 'No se encontraron registros asociados';
  @Input() tableActions: TableActionType[] = [];
  @Input() frozenColumns: string[] = []; // Keys of columns to freeze
  @Input() showCheckboxes = false; // Enable row selection
  @Input() itemIdField = 'id'; // Field to use as unique identifier for selection
  @Input() selectedIds = new Set<string | number>(); // IDs of selected items from parent
  @Input() maxSelections: number | null = null; // Maximum number of selections allowed
  /** Optional ActionMenu configuration - when provided, uses ActionMenuComponent instead of default actions */
  @Input() actionMenuConfig: ActionMenuConfig | null = null;
  @Input() actionMenuConfigFn: ((item: T) => ActionMenuConfig) | null = null;
  @Output() filterEmitter = new EventEmitter<FilterTableType>();
  @Output() selectionChange = new EventEmitter<T[]>();
  /** Event emitted when an action from ActionMenu is clicked */
  @Output() actionMenuClick = new EventEmitter<ActionMenuEvent<T>>();
  /** Event emitted when a boolean-icon cell is clicked */
  @Output() booleanIconClick = new EventEmitter<{ column: string; item: T }>();
  @ViewChild('tableContainer', { static: false })
  tableContainerRef!: ElementRef<HTMLDivElement>;

  orderEnum = OrderEnum;
  hasVerticalScroll = false;
  selectedItems = new Set<T>();
  allSelected = false;

  isDisabledSearch = true;
  currentSort: SortType = {
    orderBy: '',
    orderType: OrderEnum.DESC
  };

  private tableChangeSubscription?: Subscription;
  private resizeObserver?: ResizeObserver;
  columns: string[] = [];
  menuOptions!: SelectOption<string>[];

  tableForm: FormGroup = new FormGroup({
    search: new FormControl(''),
    searchBy: new FormControl('')
  });

  isModalOpen = false;
  modalTitle: string | undefined = '';
  // Este codigo fue generado por Github Copilot Chat
  private _modalData: T | null = null;
  // Este codigo fue generado por Github Copilot Chat
  modalWidth: number | undefined = 50;
  modalHeight: number | undefined = 30;

  get modalData(): string | string[][] {
    if (!this._modalData) return '';

    // Convertir el objeto T a string o string[][]
    if (typeof this._modalData === 'string') {
      return this._modalData;
    }

    // Si es un objeto, convertirlo a string
    return JSON.stringify(this._modalData, null, 2);
  }

  set modalData(value: T | null) {
    this._modalData = value;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly datePipe: DatePipe,
    private readonly ngZone: NgZone
  ) {}

  getColumnClass(columnKey: string, item: T): string {
    if (columnKey === 'status') {
      const statusValue = item[columnKey] as string;
      // Usar el pipe StatusColor manualmente para obtener la clase CSS
      switch (statusValue?.toLowerCase()) {
        case 'activo':
        case 'active':
          return 'status-active';
        case 'inactivo':
        case 'inactive':
          return 'status-inactive';
        case 'pendiente':
        case 'pending':
          return 'status-pending';
        default:
          return '';
      }
    }

    // Aplicar clases de prioridad si la columna es de tipo priority
    const column = this.visibleColumns.find((col) => col.key === columnKey);
    if (
      column &&
      (column as unknown as { type?: string }).type === 'priority'
    ) {
      const priorityValue = item[columnKey];
      const classes = this.getPriorityClasses(priorityValue);
      return `${classes.bg} ${classes.text}`;
    }

    // Apply alignment class if column has align property
    if (column?.align) {
      return this.getAlignmentClass(column.align);
    }

    return '';
  }

  ngOnInit() {
    this.tableForm.get('search')?.disable();
    this.initializeColumns();
    this.buildSelectOptions();
    this.handleForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items']) {
      this.initializeColumns();
      // Recalculate frozen columns when items change (e.g., page size change)
      if (!changes['items'].firstChange) {
        setTimeout(() => this.updateFrozenColumnPositions(), 0);
      }
      this.updateAllSelectedState();
    }

    // Solo establecer ordenamiento inicial si orderByDefault es true
    if (
      this.orderByDefault &&
      changes['visibleColumns'] &&
      this.visibleColumns.length > 0
    ) {
      this.currentSort.orderBy = this.visibleColumns[0].key;
    }

    // Update frozen column positions when frozenColumns input changes
    if (changes['frozenColumns'] && !changes['frozenColumns'].firstChange) {
      this.updateFrozenColumnPositions();
    }

    // Update allSelected state when selectedIds change from parent
    if (changes['selectedIds']) {
      this.updateAllSelectedState();
    }
  }

  isPriorityColumn(column: ColumnConfig): boolean {
    return column.type === 'priority';
  }

  isBooleanIconColumn(column: ColumnConfig): boolean {
    return column.type === 'boolean-icon';
  }

  isBooleanIconClickable(column: ColumnConfig): boolean {
    return column.type === 'boolean-icon' && column['clickable'] === true;
  }

  onBooleanIconClick(column: ColumnConfig, item: T, event: Event): void {
    event.stopPropagation();
    if (this.isBooleanIconClickable(column) && item[column.key]) {
      this.booleanIconClick.emit({ column: column.key, item });
    }
  }

  isBooleanTextColumn(column: ColumnConfig): boolean {
    return column.type === 'boolean-text';
  }

  getBooleanIcon(column: ColumnConfig): string {
    return column.booleanIcon ?? '';
  }

  getBooleanText(column: ColumnConfig, value: unknown): string {
    // Handle null/undefined as no data available
    if (value === null || value === undefined) {
      return '-';
    }
    const trueText = column.booleanTrueText ?? 'SI';
    const falseText = column.booleanFalseText ?? 'NO';
    return value ? trueText : falseText;
  }

  isExamTypeColumn(column: ColumnConfig): boolean {
    return column.type === 'examType';
  }

  getExamTypeBadgeClasses(examType: unknown): string {
    const classes = this.getExamTypeClasses(examType);
    return `${classes.bg} ${classes.text}`;
  }

  getExamTypeClasses(examType: unknown): { bg: string; text: string } {
    if (!examType) {
      return { bg: 'bg-exam-default-bg', text: 'text-exam-default-text' };
    }

    const examTypeStr = String(examType).toUpperCase();

    // Match por palabras clave en el tipo de examen
    if (examTypeStr.includes('TRIO')) {
      return { bg: 'bg-exam-trio-bg', text: 'text-exam-trio-text' };
    }
    if (examTypeStr.includes('DUO')) {
      return { bg: 'bg-exam-duo-bg', text: 'text-exam-duo-text' };
    }
    if (examTypeStr.includes('PANEL')) {
      return { bg: 'bg-exam-panel-bg', text: 'text-exam-panel-text' };
    }
    if (examTypeStr.includes('EXOMA')) {
      return { bg: 'bg-exam-exoma-bg', text: 'text-exam-exoma-text' };
    }
    if (examTypeStr.includes('GENOMA')) {
      return { bg: 'bg-exam-genoma-bg', text: 'text-exam-genoma-text' };
    }
    if (examTypeStr.includes('MLPA')) {
      return { bg: 'bg-exam-mlpa-bg', text: 'text-exam-mlpa-text' };
    }
    if (examTypeStr.includes('MICROARREGLOS')) {
      return { bg: 'bg-exam-microarreglos-bg', text: 'text-exam-microarreglos-text' };
    }
    if (examTypeStr.includes('EXPANSION') || examTypeStr.includes('TRIPLETA')) {
      return { bg: 'bg-exam-expansion-bg', text: 'text-exam-expansion-text' };
    }
    if (examTypeStr.includes('TAMIZAJE') || examTypeStr.includes('NEONATAL')) {
      return { bg: 'bg-exam-tamizaje-bg', text: 'text-exam-tamizaje-text' };
    }
    if (examTypeStr.includes('FUTUROS') || examTypeStr.includes('PADRES')) {
      return { bg: 'bg-exam-futuros-bg', text: 'text-exam-futuros-text' };
    }

    return { bg: 'bg-exam-default-bg', text: 'text-exam-default-text' };
  }

  getPriorityBadgeClasses(priority: unknown): string {
    const classes = this.getPriorityClasses(priority);
    return `${classes.bg} ${classes.text}`;
  }

  getPriorityDotClass(priority: unknown): string {
    const classes = this.getPriorityClasses(priority);
    return classes.dot;
  }

  getPriorityClasses(priority: unknown): {
    bg: string;
    text: string;
    dot: string;
  } {
    if (!priority) {
      return {
        bg: 'bg-priority-standard-bg',
        text: 'text-priority-standard-text',
        dot: 'bg-priority-standard-dot'
      };
    }
    // Convert priority to string safely
    let priorityStr: string;
    if (typeof priority === 'string') {
      priorityStr = priority;
    } else if (typeof priority === 'number') {
      priorityStr = priority.toString();
    } else {
      // For complex objects, use JSON.stringify
      priorityStr = JSON.stringify(priority);
    }
    const normalizedPriority = priorityStr.toUpperCase();

    switch (normalizedPriority) {
      case 'ADMINISTRATIVA':
        return {
          bg: 'bg-priority-administrativa-bg',
          text: 'text-priority-administrativa-text',
          dot: 'bg-priority-administrativa-dot'
        };
      case 'INSTITUCION':
        return {
          bg: 'bg-priority-institucion-bg',
          text: 'text-priority-institucion-text',
          dot: 'bg-priority-institucion-dot'
        };
      case 'CLINICA':
        return {
          bg: 'bg-priority-clinica-bg',
          text: 'text-priority-clinica-text',
          dot: 'bg-priority-clinica-dot'
        };
      case 'ESTANDAR':
      default:
        return {
          bg: 'bg-priority-standard-bg',
          text: 'text-priority-standard-text',
          dot: 'bg-priority-standard-dot'
        };
    }
  }

  isStatusColumn(column: ColumnConfig): boolean {
    return column.type === 'status';
  }

  getStatusBadgeClasses(status: unknown): string {
    const classes = this.getStatusClasses(status);
    return `${classes.bg} ${classes.text}`;
  }

  getStatusDotClass(status: unknown): string {
    const classes = this.getStatusClasses(status);
    return classes.dot;
  }

  getStatusClasses(status: unknown): {
    bg: string;
    text: string;
    dot: string;
  } {
    if (!status) {
      return {
        bg: 'bg-status-default-bg',
        text: 'text-status-default-text',
        dot: 'bg-status-default-dot'
      };
    }

    let statusStr: string;
    if (typeof status === 'string') {
      statusStr = status;
    } else {
      statusStr = String(status);
    }
    const normalizedStatus = statusStr.toUpperCase();

    // Reportado - Rojo
    if (normalizedStatus.includes('REPORTADO')) {
      return {
        bg: 'bg-status-reportado-bg',
        text: 'text-status-reportado-text',
        dot: 'bg-status-reportado-dot'
      };
    }
    // En análisis / En gestión - Amarillo/Naranja
    if (
      normalizedStatus.includes('ANÁLISIS') ||
      normalizedStatus.includes('ANALISIS') ||
      normalizedStatus.includes('GESTIÓN') ||
      normalizedStatus.includes('GESTION')
    ) {
      return {
        bg: 'bg-status-analisis-bg',
        text: 'text-status-analisis-text',
        dot: 'bg-status-analisis-dot'
      };
    }
    // Resuelto / Solucionado - Verde
    if (
      normalizedStatus.includes('RESUELTO') ||
      normalizedStatus.includes('SOLUCIONADO')
    ) {
      return {
        bg: 'bg-status-resuelto-bg',
        text: 'text-status-resuelto-text',
        dot: 'bg-status-resuelto-dot'
      };
    }

    // Default
    return {
      bg: 'bg-status-default-bg',
      text: 'text-status-default-text',
      dot: 'bg-status-default-dot'
    };
  }

  /**
   * Get CSS class for column alignment
   * @param align Column alignment value ('left' | 'center' | 'right')
   * @returns CSS class string for text alignment
   */
  private getAlignmentClass(align: 'left' | 'center' | 'right'): string {
    switch (align) {
      case 'right':
        return 'text-right';
      case 'center':
        return 'text-center';
      default:
        return '';
    }
  }

  ngAfterViewInit(): void {
    this.initFrozenColumnsObserver();
  }

  ngAfterViewChecked(): void {
    const tableElement = this.tableContainerRef.nativeElement;
    if (tableElement.clientWidth !== tableElement.scrollWidth) {
      this.hasVerticalScroll = true;
      this.cdr.detectChanges();
    }
  }

  /**
   * Initialize ResizeObserver for frozen columns recalculation
   * Runs outside Angular zone for better performance
   */
  private initFrozenColumnsObserver(): void {
    if (this.frozenColumns.length === 0) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const table =
        this.tableContainerRef?.nativeElement?.querySelector('table');
      if (!table || !(table instanceof HTMLTableElement)) {
        return;
      }

      const headerRow = table.querySelector('thead tr');
      if (!headerRow) {
        return;
      }

      // Observe header row and first body row for size changes
      this.resizeObserver = new ResizeObserver(() => {
        this.updateFrozenColumnPositions();
      });

      this.resizeObserver.observe(headerRow);

      // Also observe first body row to detect content changes
      const firstBodyRow = table.querySelector('tbody tr');
      if (firstBodyRow) {
        this.resizeObserver.observe(firstBodyRow);
      }

      // Initial calculation
      this.updateFrozenColumnPositions();
    });
  }

  /**
   * Calculate and apply left positions for frozen columns dynamically
   */
  private updateFrozenColumnPositions(): void {
    const table = this.tableContainerRef?.nativeElement?.querySelector('table');
    if (!table || !(table instanceof HTMLTableElement)) {
      return;
    }

    this.clearAllFrozenStyles(table);

    if (this.frozenColumns.length > 0) {
      this.applyFrozenColumnOffsets(table);
    }
  }

  /**
   * Clear frozen column inline styles only from frozen cells (optimized)
   */
  private clearAllFrozenStyles(table: HTMLTableElement): void {
    // Only clear styles from frozen columns, not all cells
    const frozenCells = Array.from(table.querySelectorAll('.frozen-column'));
    for (const cell of frozenCells) {
      const htmlCell = cell as HTMLElement;
      htmlCell.style.left = '';
      htmlCell.style.zIndex = '';
      htmlCell.style.backgroundColor = '';
      htmlCell.style.width = '';
      htmlCell.style.minWidth = '';
      htmlCell.style.maxWidth = '';
    }
  }

  /**
   * Apply frozen column offsets using optimized read/write batching
   * Reads dimensions from headers only, then writes styles in a single frame
   * This avoids reading from every cell, preventing layout thrashing
   */
  private applyFrozenColumnOffsets(table: HTMLTableElement): void {
    // 1) Read only from header cells (single read phase)
    const headerRow = table.querySelector('thead tr');
    if (!headerRow) {
      return;
    }

    const headerCells = Array.from(
      headerRow.querySelectorAll('th')
    ) as HTMLElement[];

    // Store widths and indices for frozen columns
    const frozenColumnData: { visualIndex: number; width: number }[] = [];

    for (
      let visualIndex = 0;
      visualIndex < this.visibleColumns.length;
      visualIndex++
    ) {
      const column = this.visibleColumns[visualIndex];
      if (this.isColumnFrozen(column.key)) {
        const headerCell = headerCells[visualIndex];
        const naturalWidth = headerCell?.offsetWidth ?? 0;
        // Ensure minimum width of 150px for frozen columns to prevent squashing
        // This accommodates longer content like dates and ensures sort icon is visible
        const width = Math.max(naturalWidth, 150);
        frozenColumnData.push({ visualIndex, width });
      }
    }

    // 2) Batch all DOM writes in requestAnimationFrame
    requestAnimationFrame(() => {
      let cumulativeLeft = 0;

      for (const { visualIndex, width } of frozenColumnData) {
        // 3) Write styles only to frozen cells in this column (batch write)
        const frozenCells = Array.from(
          table.querySelectorAll(
            `[data-column-index="${visualIndex}"].frozen-column`
          )
        );

        for (const cell of frozenCells) {
          const htmlCell = cell as HTMLElement;
          htmlCell.style.left = `${cumulativeLeft}px`;
          htmlCell.style.width = `${width}px`;
          htmlCell.style.minWidth = `${width}px`;
          htmlCell.style.maxWidth = `${width}px`;
        }

        cumulativeLeft += width;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.tableChangeSubscription) {
      this.tableChangeSubscription.unsubscribe();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private handleForm() {
    this.tableChangeSubscription = this.tableForm
      .get('search')!
      .valueChanges.pipe(debounceTime(2000))
      .subscribe(() =>
        this.filterEmitter.emit({
          ...(this.tableForm.value as FilterTableType),
          ...this.currentSort
        } as FilterTableType)
      );
  }

  sort(orderBy: string, orderType: OrderEnum) {
    this.currentSort = { orderBy: orderBy, orderType };
    this.filterEmitter.emit({
      ...(this.tableForm.value as FilterTableType),
      ...this.currentSort
    } as FilterTableType);
  }

  setInputDisableState(val: string) {
    if (val) {
      this.tableForm.get('search')?.enable();
      this.isDisabledSearch = false;
    } else {
      this.isDisabledSearch = true;
      this.tableForm.get('search')?.disable();
    }
    this.resetSearchInput();
  }

  private resetSearchInput() {
    this.tableForm.get('search')?.setValue('');
  }

  private buildSelectOptions() {
    this.menuOptions = this.visibleColumns
      .filter((column) => column.searchable !== false)
      .map((column) => ({
        value: camelCaseToSnake(column.key),
        label: column.label
      }));

    this.menuOptions.unshift({ label: 'selecciona una columna', value: '' });
  }

  private initializeColumns(): void {
    if (this.items && this.items.length > 0) {
      this.columns = Object.keys(this.items[0]);
    }
  }

  private assignModalVariables(action: TableActionType, item: T): void {
    this.modalTitle = action.modalTitle;
    this.modalData = (item as Record<string, unknown>)[
      action.functionProperty!
    ] as T;
    if (action.modalWidth !== undefined) {
      this.modalWidth = action.modalWidth;
    }
    if (action.modalHeight !== undefined) {
      this.modalHeight = action.modalHeight;
    }
    this.isModalOpen = true;
  }

  handleActionClick(action: TableActionType, item: T): void {
    // Don't execute action if disabled
    if (action.isDisabled?.(item)) {
      return;
    }

    if (action.openModal && action.functionProperty) {
      this.assignModalVariables(action, item);
    } else if (action.functionProperty) {
      const itemRecord = item as Record<string, unknown>;
      action.function?.(itemRecord[action.functionProperty] as number);
    } else {
      const itemRecord = item as Record<string, unknown>;
      action.function?.((itemRecord['id'] ?? itemRecord['uuid']) as number);
    }
  }

  /**
   * Handle action menu click event - forwards to parent component
   */
  onActionMenuClick(event: ActionMenuEvent<T>): void {
    this.actionMenuClick.emit(event);
  }

  /**
   * Check if action menu should be used instead of default actions
   */
  get useActionMenu(): boolean {
    return this.actionMenuConfig !== null || this.actionMenuConfigFn !== null;
  }

  getActionMenuConfigForItem(item: T): ActionMenuConfig {
    if (this.actionMenuConfigFn) {
      return this.actionMenuConfigFn(item);
    }
    return this.actionMenuConfig!;
  }

  /**
   * Get tooltip for an action button
   */
  getActionTooltip(action: TableActionType, item: T): string {
    if (!action.tooltip) {
      return '';
    }

    if (typeof action.tooltip === 'function') {
      return action.tooltip(item);
    }

    return action.tooltip;
  }

  /**
   * Check if a column is frozen (pinned)
   */
  isColumnFrozen(columnKey: string): boolean {
    if (columnKey === 'actions') {
      return false;
    }
    return this.frozenColumns.includes(columnKey);
  }

  // Métodos de selección
  toggleSelectAll(isChecked: boolean): void {
    if (isChecked) {
      if (this.maxSelections !== null && this.maxSelections > 0) {
        const availableSlots = this.maxSelections - this.selectedIds.size;

        if (availableSlots <= 0) {
          this.allSelected = false;
          this.selectionChange.emit([]);
          return;
        }

        const itemsToSelect = this.items.slice(0, availableSlots);
        this.selectionChange.emit(itemsToSelect);

        // Only mark allSelected if ALL visible items were selected
        this.allSelected = itemsToSelect.length === this.items.length;
      } else {
        this.selectionChange.emit(this.items);
        this.allSelected = true;
      }
    } else {
      this.selectionChange.emit([]);
      this.allSelected = false;
    }

    this.cdr.markForCheck();
  }

  toggleSelectItem(item: T, isChecked: boolean): void {
    const itemId = item[this.itemIdField] as string | number;

    if (isChecked) {
      const currentSelected = this.items.filter((i) => {
        const id = i[this.itemIdField] as string | number;
        return this.selectedIds.has(id);
      });
      this.selectionChange.emit([...currentSelected, item]);
    } else {
      const remainingSelected = this.items.filter((i) => {
        const id = i[this.itemIdField] as string | number;
        return this.selectedIds.has(id) && id !== itemId;
      });
      this.selectionChange.emit(remainingSelected);
    }
  }

  /**
   * Handle click on checkbox cell - intercept before checkbox changes state
   * This allows us to prevent the click when max selections reached
   */
  onCheckboxClick(event: Event, item: T): void {
    const itemId = item[this.itemIdField] as string | number;
    const isCurrentlySelected = this.selectedIds.has(itemId);

    // If trying to SELECT (not deselect) and max limit reached
    if (
      !isCurrentlySelected &&
      this.maxSelections !== null &&
      this.maxSelections > 0 &&
      this.selectedIds.size >= this.maxSelections
    ) {
      event.preventDefault();
      event.stopPropagation();

      const currentSelected = this.getCurrentlySelectedItems();
      this.selectionChange.emit([...currentSelected, item]);
    }
  }

  private getCurrentlySelectedItems(): T[] {
    return this.items.filter((item) => {
      const id = item[this.itemIdField] as string | number;
      return this.selectedIds.has(id);
    });
  }

  isItemSelected(item: T): boolean {
    const itemId = item[this.itemIdField] as string | number;

    return this.selectedIds.has(itemId);
  }

  private emitSelection(): void {
    const selectedItems = this.items.filter((item) => {
      const itemId = item[this.itemIdField] as string | number;
      return this.selectedIds.has(itemId);
    });
    this.selectionChange.emit(selectedItems);
  }

  /**
   * Get tooltip text for a column based on its configuration
   */
  getTooltipText(column: ColumnConfig, item: T): string {
    if (!column.tooltip) {
      return String(item[column.key] ?? '');
    }

    const tooltipConfig = column.tooltip;
    const field = tooltipConfig.field || column.key;
    const value = item[field];

    if (!value) {
      return String(item[column.key] ?? '');
    }

    let tooltipText = tooltipConfig.prefix ? `${tooltipConfig.prefix} ` : '';

    // Apply pipe if specified
    if (tooltipConfig.pipe === 'fullDateTime') {
      const timezone =
        tooltipConfig.timezone || TableComponent.DEFAULT_TIMEZONE;
      tooltipText +=
        this.datePipe.transform(
          this.parseDate(value),
          'dd/MM/yyyy HH:mm',
          timezone
        ) || String(value);
    } else if (tooltipConfig.pipe === 'formattedDate') {
      const timezone =
        tooltipConfig.timezone || TableComponent.DEFAULT_TIMEZONE;
      tooltipText +=
        this.datePipe.transform(
          this.parseDate(value),
          'dd/MM/yyyy',
          timezone
        ) || String(value);
    } else {
      tooltipText += String(value);
    }

    return tooltipText;
  }

  /**
   * Check if tooltip should always be shown for a column
   */
  shouldAlwaysShowTooltip(column: ColumnConfig): boolean {
    return column.tooltip?.alwaysShow === true;
  }

  /**
   * Get display value for a cell based on column configuration
   */
  getCellDisplayValue(column: ColumnConfig, item: T): string {
    const value = item[column.key];

    if (!value) {
      return '';
    }

    // Apply pipe based on column type or explicit pipe
    const pipeToUse = this.getPipeToUse(column);
    const timezone = column.timezone || TableComponent.DEFAULT_TIMEZONE;

    if (pipeToUse === 'fullDateTime') {
      return (
        this.datePipe.transform(
          this.parseDate(value),
          'yyyy-MM-dd HH:mm',
          timezone
        ) || String(value)
      );
    } else if (pipeToUse === 'formattedDate') {
      return (
        this.datePipe.transform(
          this.parseDate(value),
          'yyyy-MM-dd',
          timezone
        ) || String(value)
      );
    }

    return String(value);
  }

  /**
   * Get prefix icon configuration for a cell if configured
   * Returns type-safe icon config from column or item property
   */
  getPrefixIcon(
    column: ColumnConfig,
    item: T
  ): { iconClass: string; title: string } | null {
    if (!column.prefixIcon) {
      return null;
    }

    // If prefixIcon starts with 'item.', it's a reference to item property
    if (column.prefixIcon.startsWith('item.')) {
      const propertyKey = column.prefixIcon.substring(5);
      const value = item[propertyKey];
      // Value should be IconConfig object or null
      return value as { iconClass: string; title: string } | null;
    }

    // Otherwise it's a static icon class
    return { iconClass: column.prefixIcon, title: '' };
  }

  /**
   * Get suffix icon configuration for a cell if configured
   * Returns type-safe icon config from column or item property
   */
  getSuffixIcon(
    column: ColumnConfig,
    item: T
  ): { iconClass: string; title: string } | null {
    if (!column.suffixIcon) {
      return null;
    }

    // If suffixIcon starts with 'item.', it's a reference to item property
    if (column.suffixIcon.startsWith('item.')) {
      const propertyKey = column.suffixIcon.substring(5);
      const value = item[propertyKey];
      // Value should be IconConfig object or null
      return value as { iconClass: string; title: string } | null;
    }

    // Otherwise it's a static icon class
    return { iconClass: column.suffixIcon, title: '' };
  }

  /**
   * Check if column should render collaborators component
   */
  isCollaboratorsColumn(column: ColumnConfig): boolean {
    return column.type === 'collaborators';
  }

  /**
   * Parse date string to Date object
   * Handles dd/MM/yyyy HH:mm:ss format and ISO strings
   */
  private parseDate(value: unknown): DateValue {
    if (!value) {
      return '';
    }

    let dateValue: DateValue = value as DateValue;

    // Si es un string en formato dd/MM/yyyy HH:mm:ss, convertirlo a Date
    if (typeof value === 'string' && value.includes('/')) {
      dateValue = this.parseDateFromString(value);
    }

    return dateValue;
  }

  /**
   * Parse date from string format dd/MM/yyyy HH:mm:ss
   */
  private parseDateFromString(value: string): Date | string {
    try {
      const parts = value.split(' ');
      if (parts.length < 1) {
        return value;
      }

      const dateParts = parts[0].split('/');
      if (dateParts.length !== 3) {
        return value;
      }

      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const year = parseInt(dateParts[2], 10);

      if (parts.length === 2) {
        return this.parseDateWithTime(year, month, day, parts[1]) ?? value;
      }

      return new Date(year, month, day);
    } catch (e) {
      console.warn('Failed to parse date string:', value, e);
      return value;
    }
  }

  /**
   * Parse time part and create Date with time
   */
  private parseDateWithTime(
    year: number,
    month: number,
    day: number,
    timePart: string
  ): Date | null {
    const timeParts = timePart.split(':');
    if (timeParts.length !== 3) {
      return null;
    }

    const hour = parseInt(timeParts[0], 10);
    const minute = parseInt(timeParts[1], 10);
    const second = parseInt(timeParts[2], 10);

    return new Date(year, month, day, hour, minute, second);
  }

  /**
   * Get the pipe to use for a column based on its configuration
   */
  private getPipeToUse(column: ColumnConfig): string | null {
    if (column.pipe) {
      return column.pipe;
    }

    if (column.type === 'date') {
      return 'formattedDate';
    }

    if (column.type === 'datetime') {
      return 'fullDateTime';
    }

    return null;
  }

  /*
   * Update allSelected state based on current page items
   * Called when selectedIds change from parent component
   */
  private updateAllSelectedState(): void {
    // Check if all items on current page are selected
    if (this.items.length === 0) {
      this.allSelected = false;
      return;
    }

    const allItemsSelected = this.items.every((item) => {
      const itemId = item[this.itemIdField] as string | number;
      return this.selectedIds.has(itemId);
    });

    this.allSelected = allItemsSelected;
    this.cdr.markForCheck();
  }

  protected readonly Boolean = Boolean;
}
// Este codigo fue generado por Github Copilot Chat
